import React, { useState, useEffect } from 'react';

/**
 * @description Formulario para crear o editar una orden de trabajo.
 * Si se proporciona una `workOrder` en las props, el formulario se inicializa con sus datos para edición.
 * @param {object} props Las propiedades del componente.
 * @param {object} [props.workOrder] El objeto de la orden de trabajo a editar. Opcional.
 * @param {Array<object>} [props.projects=[]] Lista de proyectos para seleccionar.
 * @param {Array<object>} [props.technicians=[]] Lista de técnicos para asignar.
 * @param {Function} props.onSave Callback que se ejecuta al enviar el formulario.
 * @param {Function} props.onCancel Callback que se ejecuta al cancelar.
 * @returns {JSX.Element}
 */
const STATUS_OPTIONS = [
  { value: 'PENDING',     label: 'Pendiente' },
  { value: 'ASSIGNED',    label: 'Asignada' },
  { value: 'IN_PROGRESS', label: 'En progreso' },
  { value: 'COMPLETED',   label: 'Completada' },
  { value: 'CANCELED',    label: 'Cancelada' },
];

const WorkOrderForm = ({ workOrder, projects = [], technicians = [], onSave, onCancel }) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [status, setStatus] = useState('PENDING');
  const [assignedTechnicianId, setAssignedTechnicianId] = useState('');

  useEffect(() => {
    if (workOrder) {
      setOrderNumber(workOrder.orderNumber || '');
      setTitle(workOrder.title || '');
      setDescription(workOrder.description || '');
      setProjectId(workOrder.project?.id || '');
      setPriority(workOrder.priority || 'MEDIUM');
      setStatus(workOrder.status || 'PENDING');
      setAssignedTechnicianId(workOrder.assignedTechnician?.id || '');
    } else {
      setOrderNumber('');
      setTitle('');
      setDescription('');
      setProjectId('');
      setPriority('MEDIUM');
      setStatus('PENDING');
      setAssignedTechnicianId('');
    }
  }, [workOrder]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!projectId) {
      alert('Por favor, seleccione un proyecto.');
      return;
    }
    const data = {
      title,
      description,
      priority,
      projectId: parseInt(projectId, 10),
      // Solo enviar el número de orden si tiene valor — string vacío no debe llegar al backend
      ...(orderNumber.trim() && { orderNumber: orderNumber.trim() }),
    };
    // El estado solo se envía en edición — al crear lo asigna el backend según si hay técnico asignado
    if (workOrder) {
      data.status = status;
    }
    if (assignedTechnicianId) {
      data.assignedTechnicianId = parseInt(assignedTechnicianId, 10);
    }
    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="wo-order-number">N.° de orden (opcional)</label>
        <input
          id="wo-order-number"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          placeholder="Ej: OT-2025-001"
          maxLength={50}
          className="modal-field"
          style={{ fontFamily: 'monospace' }}
        />
      </div>
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="wo-title">Título</label>
        <input
          id="wo-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título de la orden"
          required
          className="modal-field"
        />
      </div>
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="wo-description">Descripción</label>
        <textarea
          id="wo-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descripción del trabajo a realizar"
          rows={3}
          className="modal-field modal-field-textarea"
        />
      </div>
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="wo-project">Proyecto</label>
        <select
          id="wo-project"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          required
          className="modal-field"
        >
          <option value="">Seleccione un proyecto</option>
          {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
        </select>
      </div>
      {/* Selector de prioridad — mapea al enum OrderPriority del backend */}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="wo-priority">Prioridad</label>
        <select
          id="wo-priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="modal-field"
        >
          <option value="LOW">Baja</option>
          <option value="MEDIUM">Media</option>
          <option value="HIGH">Alta</option>
          <option value="URGENT">Urgente</option>
        </select>
      </div>
      {/* Selector de estado — solo visible en edición, mapea al enum OrderStatus del backend */}
      {workOrder && (
        <div className="modal-field-group">
          <label className="modal-field-label" htmlFor="wo-status">Estado</label>
          <select
            id="wo-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="modal-field"
          >
            {STATUS_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      )}
      <div className="modal-field-group">
        <label className="modal-field-label" htmlFor="wo-technician">Técnico asignado (opcional)</label>
        <select
          id="wo-technician"
          value={assignedTechnicianId}
          onChange={(e) => setAssignedTechnicianId(e.target.value)}
          className="modal-field"
        >
          <option value="">Sin asignar</option>
          {technicians.map(t => <option key={t.user_id} value={t.user_id}>{t.username}</option>)}
        </select>
      </div>

      <div className="modal-footer">
        <button type="button" onClick={onCancel} className="modal-btn-secondary">Cancelar</button>
        <button type="submit" className="modal-btn-primary">Guardar</button>
      </div>
    </form>
  );
};

// El gap entre campos vive acá porque Modal.js solo controla el espaciado
// de su único hijo directo (el <form>).
const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
};

export default WorkOrderForm;
